import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { z } from "zod";
import { seo } from "@/lib/seo";
import { fetchCheckoutStatus } from "@/lib/cashfree.functions";
import { MarketingPage } from "@/components/marketing/marketing-kit";

export const Route = createFileRoute("/payment-status")({
  validateSearch: (search: Record<string, unknown>) =>
    z.object({ order_id: z.string().trim().min(1).optional() }).parse(search),
  head: () =>
    seo({
      title: "Payment status | The Quantum Mind",
      description:
        "Confirmation screen for your The Quantum Mind premium subscription payment.",
      path: "/payment-status",
      noindex: true,
    }),
  component: PaymentStatusPage,
});

function PaymentStatusPage() {
  const { order_id: orderId } = Route.useSearch();
  const checkStatus = useServerFn(fetchCheckoutStatus);

  const statusQuery = useQuery({
    queryKey: ["checkout-status", orderId],
    queryFn: () => checkStatus({ data: { order_id: orderId as string } }),
    enabled: Boolean(orderId),
    // Cashfree may still be settling when the browser returns; poll briefly.
    refetchInterval: (query) =>
      query.state.data && query.state.data.status !== "pending" ? false : 4000,
    retry: 1,
  });

  const status = statusQuery.data?.status;

  return (
    <MarketingPage>
      <div className="glass mx-auto max-w-md rounded-3xl border border-hairline p-6 text-center">
        {!orderId ? (
          <Panel
            tone="error"
            title="No payment to show"
            body="We couldn't find an order reference in this link."
          />
        ) : statusQuery.isLoading ? (
          <div className="py-8">
            <Loader2 className="mx-auto size-8 animate-spin text-primary" aria-hidden />
            <p className="mt-4 text-sm font-semibold">Confirming your payment…</p>
            <p className="mt-1 text-xs text-muted-foreground">
              We're checking directly with Cashfree. This takes a few seconds.
            </p>
          </div>
        ) : statusQuery.isError ? (
          <Panel
            tone="error"
            title="We couldn't check this payment"
            body="Please refresh, or contact support if money was debited."
          />
        ) : status === "approved" ? (
          <Panel
            tone="success"
            title="Payment successful"
            body="Your premium subscription is active. Every tracker is now unlimited."
          />
        ) : status === "cancelled" ? (
          <Panel
            tone="error"
            title="Payment cancelled"
            body="You closed the checkout before finishing. Nothing was charged."
          />
        ) : status === "failed" || status === "rejected" ? (
          <Panel
            tone="error"
            title="Payment failed"
            body="The payment did not go through. If money was debited it will be refunded automatically by your bank."
          />
        ) : (
          <Panel
            tone="pending"
            title="Payment pending"
            body="Cashfree hasn't confirmed this payment yet. This page updates automatically — premium activates the moment it's verified."
          />
        )}

        <div className="mt-6 flex flex-col gap-2">
          <Link
            to="/"
            className="rounded-full bg-primary px-4 py-3 text-xs font-bold text-primary-foreground"
          >
            Go to my dashboard
          </Link>
          <Link
            to="/pricing"
            className="rounded-full border border-hairline px-4 py-3 text-xs font-semibold text-muted-foreground"
          >
            Back to pricing
          </Link>
        </div>

        {orderId ? (
          <p className="mt-4 text-[10px] text-muted-foreground">Order reference: {orderId}</p>
        ) : null}
      </div>
    </MarketingPage>
  );
}

function Panel({
  tone,
  title,
  body,
}: {
  tone: "success" | "error" | "pending";
  title: string;
  body: string;
}) {
  const Icon = tone === "success" ? CheckCircle2 : tone === "pending" ? Clock : XCircle;
  const color =
    tone === "success"
      ? "text-emerald-500"
      : tone === "pending"
        ? "text-amber-500"
        : "text-destructive";
  return (
    <div className="py-6">
      <Icon className={`mx-auto size-10 ${color}`} aria-hidden />
      <h1 className="mt-4 text-lg font-extrabold tracking-tight">{title}</h1>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
